/**
 * Story Clusters — filter the archive, press Analyze, get the themes several different sources
 * independently talked about, each with the quotes that make the case.
 *
 * Deep Query answers a question you already had. This one surfaces the question. The design
 * consequence is that SOURCE COUNT is the headline metric on every card, not passage count: the
 * whole claim of this screen is "more than one person said this", so that number has to be the
 * thing the eye lands on, and it has to be honest when it is only two.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE } from '../../utils/constants';
import { authHeaders } from '../../services/api';

// Same default window as Deep Query. Kept as a local constant rather than imported so this
// screen can diverge if the two ever need different windows, but the number is deliberately
// identical today.
const DEFAULT_WINDOW_DAYS = 90;
const isoDaysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// ── One story ────────────────────────────────────────────────────────────────
const ClusterCard = ({ cluster, rank, forceOpen }) => {
  // COLLAPSED by default, every card. The job of this screen is "show me the trends", and a
  // transcript excerpt runs several hundred words — two expanded cards push the third story below
  // the fold and the eighth out of sight entirely, so the one thing the screen exists to give you
  // (the shape of the whole set) is the thing you cannot see. Quotes are the evidence you open
  // when a headline earns it.
  const [open, setOpen] = useState(false);
  useEffect(() => { if (forceOpen !== null) setOpen(forceOpen); }, [forceOpen]);
  const quotes = cluster.quotes || [];
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-5 py-4 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-kc-navy text-white text-xs font-semibold flex items-center justify-center">
            {rank}
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-kc-navy leading-snug">{cluster.headline}</h3>
            {cluster.angle && <p className="text-sm text-slate-600 mt-1 leading-relaxed">{cluster.angle}</p>}
            <div className="flex items-center gap-2 mt-2">
              {/* The corroboration count, stated plainly. Two sources is a pattern worth a look;
                  four is a story. Colouring them differently stops "2 sources" from being read
                  with the same confidence as "5 sources". */}
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                cluster.source_count >= 4 ? 'bg-emerald-100 text-emerald-700'
                  : cluster.source_count === 3 ? 'bg-blue-100 text-blue-700'
                  : 'bg-slate-100 text-slate-600'}`}>
                {cluster.source_count} sources
              </span>
              <span className="text-[11px] text-slate-400">{cluster.passage_count} passages</span>
            </div>
          </div>
          <svg className={`w-4 h-4 text-slate-400 flex-shrink-0 mt-1 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-100 divide-y divide-slate-100">
          {quotes.map((q, i) => (
            <blockquote key={i} className="px-5 py-3.5">
              <p className="text-sm text-slate-700 leading-relaxed">“{q.text}”</p>
              <footer className="text-xs text-slate-500 mt-1.5">
                <span className="font-medium text-slate-700">{q.person_name || 'Unattributed'}</span>
                {q.job_title && <>, {q.job_title}</>}
                {q.company && <> · {q.company}</>}
                {q.date_str && <> · {fmtDate(q.date_str)}</>}
              </footer>
            </blockquote>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Screen ───────────────────────────────────────────────────────────────────
const StoryClustersView = () => {
  const [filters, setFilters] = useState(null);        // vocabulary from the corpus
  const [assetClasses, setAssetClasses] = useState([]);
  const [location, setLocation] = useState('');
  // Default to the last 90 days, matching Deep Query so the two screens don't disagree about
  // what "recent" means. NOTE this window is easy to hit the edge of here: asset-class tagging
  // on this corpus stopped in Dec 2025, so an asset filter plus this default currently matches
  // nothing. The backend detects that case specifically and says so rather than reporting a
  // bare "no results" — see analyze() in story_clusters_service.
  const [dateFrom, setDateFrom] = useState(isoDaysAgo(DEFAULT_WINDOW_DAYS));
  const [dateTo, setDateTo] = useState('');

  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  // null = each card keeps its own state; true/false = a bulk expand/collapse was just clicked.
  const [expandAll, setExpandAll] = useState(null);
  const [error, setError] = useState(null);

  // The filter vocabulary is read from the corpus, with counts, so a slice that will return
  // nothing is visible BEFORE the minute-long analysis rather than after it.
  //
  // Re-runs whenever the DATE RANGE changes, because the counts are scoped to it: leaving
  // "Residential 55" on screen inside a window holding none of them is an actively false promise.
  // Debounced because a native date input fires on every keystroke while someone types a year,
  // and each fire is a full pass over the interview collection.
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const qs = new URLSearchParams();
        if (dateFrom) qs.set('date_from', dateFrom);
        if (dateTo) qs.set('date_to', dateTo);
        const res = await fetch(`${API_BASE}/deepquery/cluster-filters?${qs}`, { headers: authHeaders() });
        const data = await res.json();
        if (!cancelled && data.success) {
          setFilters(data.data);
          // Drop any selected asset class that no longer exists in this window. Leaving it
          // selected-but-invisible means Analyze silently filters on something with no chip on
          // screen — the user would see zero results and nothing explaining why.
          const live = new Set((data.data.asset_classes || []).map((a) => a.value));
          setAssetClasses((cur) => cur.filter((v) => live.has(v)));
        }
      } catch { /* the screen still works with free-text filters */ }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [dateFrom, dateTo]);

  const toggleAsset = (value) => {
    setAssetClasses((cur) => cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value]);
  };

  const analyze = useCallback(async () => {
    setRunning(true); setError(null); setResult(null); setExpandAll(null);
    try {
      const res = await fetch(`${API_BASE}/deepquery/clusters`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          location: location || '',
          asset_classes: assetClasses,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        }),
      });
      const data = await res.json();
      if (data.success) setResult(data.data);
      else setError(data.error?.message || 'Analysis failed');
    } catch {
      setError('Analysis request failed');
    } finally { setRunning(false); }
  }, [location, assetClasses, dateFrom, dateTo]);

  // Rough size of the slice before running, from the filter counts. An estimate, and labelled as
  // one — the real intersection of two filters is smaller than either.
  const assetTotal = (filters?.asset_classes || [])
    .filter((a) => assetClasses.includes(a.value))
    .reduce((n, a) => n + a.count, 0);
  const locTotal = (filters?.locations || []).find((l) => l.value === location.toLowerCase())?.count;
  // Runtime scales with the slice, and badly under-promising it is worse than saying nothing: a
  // flat "~20s" on a slice that actually takes a minute reads as a hang. Measured on this corpus:
  // 9 interviews ~25s, 55 interviews ~63s.
  const sliceSize = assetTotal || locTotal || filters?.total_interviews || 0;
  const estSeconds = sliceSize > 40 ? 'takes about a minute'
    : sliceSize > 15 ? 'takes ~40s'
    : 'takes ~25s';

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-kc-navy">Story Clusters</h1>
          <p className="text-sm text-slate-500 mt-1">
            Narrow the archive, then find what several different sources independently said about the
            same thing — with the quotes to back it.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
          {/* DATE RANGE FIRST. It is not a peer of the other filters — it decides which
              interviews exist at all, and every count below it is computed within it. Putting it
              after the chips invited reading "Residential 55" as a fact about the archive rather
              than a fact about the current window. */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">From</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                data-testid="sc-date-from"
                className="w-full text-sm border border-slate-300 rounded px-2 py-1.5" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">To</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                data-testid="sc-date-to"
                className="w-full text-sm border border-slate-300 rounded px-2 py-1.5" />
            </div>
          </div>
          <p className="text-[11px] text-slate-400 -mt-2 mb-4">
            {/* Only claim a "range" when one is actually set. With both dates empty this read
                "711 interviews in this range · the counts below are within it" while no range
                existed — describing a constraint that wasn't there. */}
            {!filters ? 'Counting…'
              : (dateFrom || dateTo)
                ? <>{filters.total_interviews.toLocaleString()} interviews in this date range · the counts below are within it</>
                : <>{filters.total_interviews.toLocaleString()} interviews · the whole archive</>}
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(''); setDateTo(''); }}
                data-testid="sc-clear-dates-inline"
                className="ml-2 text-kc-blue hover:underline">Clear</button>
            )}
          </p>

          <label className="block text-xs font-medium text-slate-600 mb-2">Asset class</label>
          <div className="flex flex-wrap gap-2 mb-4">
            {(filters?.asset_classes || []).map((a) => (
              <button key={a.value} onClick={() => toggleAsset(a.value)}
                data-testid={`sc-asset-${a.value}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  assetClasses.includes(a.value)
                    ? 'bg-kc-blue text-white border-kc-blue'
                    : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}>
                {a.label} <span className="opacity-60">{a.count}</span>
              </button>
            ))}
            {!filters && <span className="text-xs text-slate-400">Loading…</span>}
            {/* An empty chip row inside a date window is a real answer, not a loading state —
                say which one it is. On this corpus it is the usual outcome of the default
                window, because asset tagging stopped in Dec 2025. */}
            {filters && (filters.asset_classes || []).length === 0 && (
              <span className="text-xs text-amber-700">
                No interviews in this date range carry an asset class — widen the dates to filter by one.
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Location</label>
              <input
                list="sc-locations" value={location} onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. Florida" data-testid="sc-location"
                className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#4A6CF7]" />
              <datalist id="sc-locations">
                {(filters?.locations || []).map((l) => <option key={l.value} value={l.label}>{l.count} interviews</option>)}
              </datalist>
              {/* State names sweep up their cities. Saying so prevents "Florida" being read as
                  the literal tag, which is how the underlying data is actually fragmented. */}
              <p className="text-[11px] text-slate-400 mt-1">
                A state includes its cities{locTotal ? ` — ${locTotal} interviews` : ''}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
            <p className="text-xs text-slate-400">
              {/* Counts the DATE RANGE as a filter, because it is one — and on this screen it is
                  the one that is always set. Calling a 97-interview window "no filters ... the
                  whole archive" was simply false. */}
              {assetClasses.length || location
                ? <>Roughly {assetTotal || locTotal || '—'} interviews before filters combine · {estSeconds}</>
                : (dateFrom || dateTo)
                  ? <>{(filters?.total_interviews ?? 0).toLocaleString()} interviews in this date range · {estSeconds}</>
                  : <>No filters — this reads the entire archive and will be slow and unfocused</>}
            </p>
            <button onClick={analyze} disabled={running} data-testid="sc-analyze"
              className="px-5 py-2 rounded-lg text-sm font-medium bg-kc-blue text-white hover:bg-kc-blue-dk disabled:opacity-50">
              {running ? 'Analysing…' : 'Analyze'}
            </button>
          </div>
        </div>

        {running && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#4A6CF7] flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-[#4A6CF7]">Reading the archive…</p>
              <p className="text-xs text-blue-500 mt-0.5">
                Grouping passages by meaning, then keeping only what more than one person said.
                Big slices can take a minute — this doesn't time out.
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {result && (
          <>
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-sm font-semibold text-slate-700">
                {result.clusters.length > 0
                  ? `${result.clusters.length} stor${result.clusters.length === 1 ? 'y' : 'ies'} in ${result.slice_label}`
                  : 'No stories found'}
              </h2>
              {/* What was read and what was thrown away. Without this a three-story result from a
                  300-passage slice looks like the tool barely tried. */}
              <span className="text-[11px] text-slate-400 flex items-center gap-2">
                <button
                  onClick={() => setExpandAll((v) => (v === true ? false : true))}
                  data-testid="sc-expand-all"
                  className="text-kc-blue hover:underline">
                  {expandAll === true ? 'Collapse all' : 'Expand all'}
                </button>
                <span>·</span>
                {result.diagnostics.interviews_matched} interviews ·{' '}
                {result.diagnostics.passages_clustered} passages
                {result.diagnostics.duplicates_dropped > 0 && <> · {result.diagnostics.duplicates_dropped} repeats removed</>}
                {result.diagnostics.boilerplate_dropped > 0 && <> · {result.diagnostics.boilerplate_dropped} boilerplate</>}
                {result.incoherent_dropped > 0 && <> · {result.incoherent_dropped} unclear</>}
              </span>
            </div>

            {result.message && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-amber-800">{result.message}</p>
                {/* One click out of the dead end. The default 90-day window is the most common
                    reason a slice comes back empty here, so the escape hatch belongs next to the
                    explanation rather than back up in the filter panel. */}
                {(dateFrom || dateTo) && (
                  <button
                    onClick={() => { setDateFrom(''); setDateTo(''); }}
                    data-testid="sc-clear-dates"
                    className="mt-2 text-xs font-medium text-amber-900 underline hover:no-underline">
                    Clear the date range
                  </button>
                )}
              </div>
            )}

            <div className="space-y-3">
              {result.clusters.map((c, i) => (
                <ClusterCard key={i} cluster={c} rank={i + 1} forceOpen={expandAll} />
              ))}
            </div>

            {result.diagnostics.truncated && (
              <p className="text-xs text-amber-600 mt-4">
                This slice is larger than one analysis can hold, so passages were sampled evenly
                across all {result.diagnostics.interviews_matched} interviews — every interview is
                represented, but not every passage was read. Narrow the filters for full depth.
              </p>
            )}
          </>
        )}

        {!result && !running && !error && (
          <div className="text-center py-12 text-slate-300">
            <svg className="w-10 h-10 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <p className="text-sm">Pick an asset class and a place, then press Analyze.</p>
            <p className="text-xs mt-1">e.g. Residential + Florida</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoryClustersView;
